import ConfigurationViewContainer from 'js/components/elements/configViews/ConfigurationViewContainer';
import { CONNECTION_CATEGORY_WEBHOOK } from 'js/constants/connectionCategories';
import { ROUTE_MY_DATA_SETS } from 'js/constants/pathnames';
import { RESOURCE_TYPE_SOURCE } from 'js/constants/resourceTypes';
import { DataSource } from 'new/entities/DataSource';
import { useCurrentOrg } from 'new/hooks/adapters/useCurrentOrg';
import React from 'react';
import { Link } from 'react-router-dom';
import { SwitchableSection } from 'new/elements/SwitchableSection/SwitchableSection';
import { JSONView } from 'new/elements/JSONView/JSONView';
import { isEmpty, map } from 'lodash';
import { SearchFilterTypes } from 'js/constants/visibilityFilters';
import { useSourceOverview } from 'new/elements/Overview/Source/useSourceOverview';
import { formatCode } from 'js/lib/FormatUtils';
import {
  formatDateRangeForApi,
  getPresetDateRange,
  MetricsDateRange,
} from '@modules/monitor/resource-triage/components/MetricsTrendPanel';
import { MetricsPanel } from '@modules/monitor/resource-triage/components/MetricsPanel';
import { useSourceOverviewMetrics } from 'new/elements/Overview/Source/useSourceOverviewMetrics';
import ApiServerConfigView from 'js/components/data_sources/cards/configViews/ApiServerConfigView';
import { shouldHideRecordsForResource } from 'js/lib/PipelineUtils';
import { CredentialWidget, useCredentialWidgetData } from 'new/elements/CredentialWidget';

import styles from '../Overview.module.scss';

interface SourceOverviewProps {
  resource: DataSource;
}

export const SourceOverview = ({ resource }: SourceOverviewProps) => {
  const org = useCurrentOrg();
  const {
    setsCount,
    createdDate,
    lastUpdate,
    resourceTypeFields,
    connectionCategory,
    sourceConfig,
    resourceApiKeys,
    credentials,
  } = useSourceOverview(resource);

  const filteredFields = resource?.flow_triggers?.length
    ? resourceTypeFields.filter((f) => f.name !== 'start.cron')
    : resourceTypeFields;

  const {
    loadResourceMetricsAggregate,
    metrics,
    isLoading,
    metricsByDate,
    loadResourceMetricsPerDay,
  } = useSourceOverviewMetrics(resource);

  const [metricsRange, setMetricsRange] = React.useState(() => getPresetDateRange('30d'));

  const onMetricsDatesChanged = (dates: MetricsDateRange) => {
    setMetricsRange({
      start: new Date(dates.start),
      end: new Date(dates.end),
    });
    loadResourceMetricsPerDay(formatDateRangeForApi(dates));
  };

  const onMetricsRefresh = () => {
    loadResourceMetricsAggregate();
    loadResourceMetricsPerDay(formatDateRangeForApi(metricsRange));
  };

  const { source_config, template_config = {} } = resource;
  const configurations = Object.keys(template_config).length
    ? { source_config, template_config }
    : { source_config };
  const configurationsJSON = JSON.stringify(configurations);

  const hasCredentials = Boolean(credentials);
  const credentialWidgetData = useCredentialWidgetData(hasCredentials ? credentials : null);
  const isWebhookConnection = connectionCategory === CONNECTION_CATEGORY_WEBHOOK;
  const webhookApiKey =
    isWebhookConnection && !isEmpty(resourceApiKeys)
      ? map(resourceApiKeys, (k) => k.api_key)
      : null;

  const credentialsContent = isWebhookConnection ? (
    webhookApiKey
  ) : credentialWidgetData ? (
    <CredentialWidget {...credentialWidgetData} />
  ) : null;

  // check if we hide records for this flow type
  const hideRecords = shouldHideRecordsForResource(resource);

  const createdOnElement = (
    <>
      <div className="detail-key">Created On</div>
      <div className="detail-value">{createdDate}</div>
    </>
  );

  const lastUpdateOnElement = (
    <>
      <div className="detail-key">Last Updated On</div>
      <div className="detail-value">{lastUpdate}</div>
    </>
  );

  return (
    <div className={styles.sourceOverviewContainer}>
      <div className="tab-content">
        <section className="tab-content-section">
          <div className="detail-row">
            <div className="detail-col">
              <div className="detail-key">Source ID</div>
              <div className="detail-value">{resource.id}</div>
              {createdOnElement}
              {hasCredentials ? (
                <>
                  <div className="detail-key">{webhookApiKey ? 'API Keys' : 'Credentials'}</div>
                  <div className="detail-value">{credentialsContent}</div>
                </>
              ) : null}
            </div>

            <div className="detail-col">
              <div className="detail-key">Detected Nexsets Count</div>
              <div className="detail-value">
                {setsCount && setsCount > 0 ? (
                  <Link
                    to={`/integrate${ROUTE_MY_DATA_SETS}?search=${SearchFilterTypes.SOURCE_ID}:${resource.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {setsCount}
                  </Link>
                ) : (
                  setsCount
                )}
              </div>
              {lastUpdateOnElement}
            </div>
          </div>
        </section>

        <hr />

        <section className="tab-content-section">
          {resource?.flow_type === 'api_server' ? (
            <ApiServerConfigView data={resource} />
          ) : (
            <SwitchableSection
              switchTitle="JSON view"
              title="Source Configuration"
              DefaultComponent={
                <ConfigurationViewContainer
                  entityType={RESOURCE_TYPE_SOURCE}
                  connectionCategory={connectionCategory}
                  config={sourceConfig}
                  configFields={filteredFields}
                  resourceId={resource.id}
                  creds={
                    credentialWidgetData ? <CredentialWidget {...credentialWidgetData} /> : null
                  }
                  currentOrg={org}
                  data={resource}
                />
              }
              SwitchableComponent={
                <div style={{ height: 300 }}>
                  <JSONView json={formatCode(configurationsJSON, 'application/json')} />
                </div>
              }
            />
          )}
        </section>

        <hr />

        <section className="tab-content-section">
          <MetricsPanel
            lifetimeMetrics={metrics}
            trendData={metricsByDate}
            start={metricsRange.start}
            end={metricsRange.end}
            onDatesChanged={onMetricsDatesChanged}
            onRefresh={onMetricsRefresh}
            hideRecords={hideRecords}
            loading={isLoading}
            resourceType={RESOURCE_TYPE_SOURCE}
            resourceId={resource.id}
          />
        </section>
      </div>
    </div>
  );
};
